import React from 'react';
import s from './CartItemThumbnail.scss';
import {ItemLink} from '../CartItem/partials/ItemLink/ItemLink';
import classNames from 'clsx';
import {ImageModel} from '../../../../domain/models/Image.model';
import {UrlModel} from '../../../../domain/models/LineItems/Url.model';
import {ThumbnailImage} from '@wix/wix-ui-tpa/cssVars';
import {getImageDimensions} from '../../../../domain/utils/itemUtilsWithSideEffect';
import {ProductType} from '@wix/wixstores-client-core';
import {Download, Document, GiftSmall} from '@wix/wix-ui-icons-common/on-stage';
import {SUB_ITEM_DEFAULT_IMAGE_ICON_SIZE, SUB_ITEM_THUMBNAIL_SIZE} from '../../../constants';

export enum CartItemThumbnailDataHook {
  Image = 'CartItemThumbnailDataHook.Image',
  DefaultImage = 'default-image',
  DefaultImageDigital = 'default-image-digital',
  DefaultImagePhysical = 'default-image-physical',
  Badge = 'product-badge',
  Wrapper = 'product-thumbnail-wrapper',
  Media = 'product-thumbnail-media',
  QuantityBadge = 'CartItemThumbnailDataHook.quantityBadge',
}

const DEFAULT_IMAGE_DEFAULT_SIZE = 50;

type MediaDimensions = {width: number; height: number};

export const CartItemThumbnail = ({
  name,
  image,
  url,
  itemType,
  className,
  quantityBadge,
  dimensions = CartItemThumbnail.defaultDimensions,
  isSubItem = false,
}: {
  name: string;
  image: ImageModel;
  url: UrlModel;
  itemType: string;
  className?: string;
  quantityBadge?: number;
  dimensions?: MediaDimensions;
  isSubItem?: boolean;
}) => {
  const imageDimensions = isSubItem
    ? {width: SUB_ITEM_THUMBNAIL_SIZE, height: SUB_ITEM_THUMBNAIL_SIZE}
    : getImageDimensions(image, dimensions);
  const defaultImageIconSize = isSubItem ? SUB_ITEM_DEFAULT_IMAGE_ICON_SIZE : DEFAULT_IMAGE_DEFAULT_SIZE;

  const renderImage = () => {
    if (!image) {
      return (
        <div
          data-hook={CartItemThumbnailDataHook.DefaultImage}
          className={classNames(s.defaultImage)}
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height,
          }}>
          {itemType === ProductType.DIGITAL ? (
            <Document
              size={defaultImageIconSize.toString()}
              data-hook={CartItemThumbnailDataHook.DefaultImageDigital}
            />
          ) : (
            <GiftSmall
              size={defaultImageIconSize.toString()}
              data-hook={CartItemThumbnailDataHook.DefaultImagePhysical}
            />
          )}
        </div>
      );
    }

    return (
      <div className={classNames(s.productImage)} data-hook={CartItemThumbnailDataHook.Wrapper}>
        <ThumbnailImage
          src={image.id ?? /* istanbul ignore next */ image.url}
          width={imageDimensions.width}
          height={imageDimensions.height}
          alt={name}
          data-hook={CartItemThumbnailDataHook.Media}
          aria-hidden={false}
        />
        {itemType === ProductType.DIGITAL && (
          <div className={classNames(s.productBadge)} data-hook={CartItemThumbnailDataHook.Badge}>
            <Download />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={classNames(s.thumbnail, className)} data-hook={CartItemThumbnailDataHook.Image}>
      <div className={isSubItem ? s.subItemThumbnailContent : s.thumbnailContent}>
        <ItemLink url={url} tabIndex={-1}>
          {renderImage()}
        </ItemLink>
      </div>
      {quantityBadge !== undefined && (
        <span data-hook={CartItemThumbnailDataHook.QuantityBadge} className={s.quantityBadge} aria-hidden="true">
          {quantityBadge}
        </span>
      )}
    </div>
  );
};

CartItemThumbnail.defaultDimensions = {width: 100, height: 100};
